// modals/FamilyReportModal.js
// -----------------------------------------------------------------------------
// Modular family report. Two tabs:
//   • This Family — a printable sheet for the open family.
//   • All Families — a filterable table + CSV export.
// The user ticks which SECTIONS to include (Registry / Parents / Children /
// Status & Contact); both the table columns and the printed sheet follow the
// same selection, so it's one modular definition (MODULES below).
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { X, Printer, Download, FileText } from 'lucide-react';

const val = (v) => (v === null || v === undefined ? '' : String(v));
const joinAr = (a, b) => [a, b].filter(Boolean).join(' ');
const nat = (a, b) => [a, b].filter(Boolean).join(' / ');
const work = (job, sector, company, addr) =>
  [job, sector ? `(${sector})` : '', company, addr].filter(Boolean).join(' · ');

// The single source of truth for what each section contains. `get` reads a
// family row (same field names from /families-report and /family-details).
const MODULES = [
  {
    key: 'registry', label: 'Lebanese Registry — القيد',
    cols: [
      { label: 'المحافظة', get: (f) => f.GovernorateNameAr },
      { label: 'القضاء', get: (f) => f.CazaNameAr },
      { label: 'المحلة', get: (f) => f.LocalityNameAr },
      { label: 'رقم السجل', get: (f) => f.RegisterNumber },
    ],
  },
  {
    key: 'parents', label: 'Parents — الأهل',
    cols: [
      { label: 'Father', get: (f) => f.FatherName },
      { label: 'الأب (عربي)', get: (f) => joinAr(f.FatherFirstNameAr, f.FatherLastNameAr) },
      { label: 'Father Nationality', get: (f) => nat(f.FatherNationality1, f.FatherNationality2) },
      { label: 'Father', get: (f) => (f.FatherDeceased ? 'Deceased' : ''), hideEmpty: true, tag: true },
      { label: 'Father Work', get: (f) => work(f.FatherJob, f.FatherJobSector, f.FatherCompany, f.FatherWorkAddress) },
      { label: 'Mother', get: (f) => f.MotherName },
      { label: 'الأم (عربي)', get: (f) => joinAr(f.MotherFirstNameAr, f.MotherLastNameAr) },
      { label: 'Mother Nationality', get: (f) => nat(f.MotherNationality1, f.MotherNationality2) },
      { label: 'Mother', get: (f) => (f.MotherDeceased ? 'Deceased' : ''), hideEmpty: true, tag: true },
      { label: 'Mother Work', get: (f) => work(f.MotherJob, f.MotherJobSector, f.MotherCompany, f.MotherWorkAddress) },
    ],
  },
  {
    key: 'status', label: 'Status & Contact',
    cols: [
      { label: 'Family Status', get: (f) => f.FamilyStatus },
      { label: 'Religion', get: (f) => f.Religion },
      { label: 'Marital Status', get: (f) => f.MaritalStatus },
      { label: 'Phone', get: (f) => f.PrimaryPhone || f.FatherPhone || f.MotherPhone },
      { label: 'Email', get: (f) => f.Email },
      { label: 'Address', get: (f) => f.Address },
    ],
  },
  {
    key: 'children', label: 'Children — الأولاد',
    cols: [{ label: '# Children', get: (f) => f.ChildrenCount }],
  },
];

const esc = (s) => val(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// mode: 'family' = printable sheet for the open family; 'all' = table + export.
const FamilyReportModal = ({ apiCall, mode = 'all', familyId, familyDetails, academicYear, showError, onClose }) => {
  const [sel, setSel] = useState({ registry: true, parents: true, children: true, status: true });
  const [all, setAll] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [search, setSearch] = useState('');
  const [children, setChildren] = useState({ current: [], historical: [] });

  const chosen = MODULES.filter((m) => sel[m.key]);

  useEffect(() => {
    if (!familyId) return;
    apiCall(`/families/${familyId}/school-children?academicYear=${encodeURIComponent(academicYear || '')}`)
      .then((r) => setChildren(r && typeof r === 'object' ? r : { current: [], historical: [] }))
      .catch(() => setChildren({ current: [], historical: [] }));
  }, [apiCall, familyId, academicYear]);

  const loadAll = useCallback(async () => {
    if (all) return;
    setLoadingAll(true);
    try {
      setAll(await apiCall('/registration/families-report') || []);
    } catch (err) {
      showError && showError('Failed to load families: ' + err.message);
      setAll([]);
    } finally {
      setLoadingAll(false);
    }
  }, [apiCall, all, showError]);

  useEffect(() => { if (mode === 'all') loadAll(); }, [mode, loadAll]);

  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));

  // ---- All Families: columns from chosen modules (children -> count column) ----
  const tableCols = [{ label: 'Family ID', get: (f) => f.FamilyID }].concat(
    chosen.flatMap((m) => m.cols.filter((c) => !c.tag).concat(
      m.key === 'parents' ? [
        { label: 'Father Status', get: (f) => (f.FatherDeceased ? 'Deceased' : '') },
        { label: 'Mother Status', get: (f) => (f.MotherDeceased ? 'Deceased' : '') },
      ] : []
    ))
  );

  const rows = (all || []).filter((f) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [f.FamilyID, f.FatherName, f.MotherName, f.FatherFirstNameAr, f.LocalityNameAr, f.CazaNameAr]
      .some((x) => val(x).toLowerCase().includes(q));
  });

  const exportCsv = () => {
    const header = tableCols.map((c) => c.label);
    const lines = [header].concat(rows.map((f) => tableCols.map((c) => val(c.get(f)))));
    const csv = '﻿' + lines.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `families-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  // ---- This Family: printable sheet of the chosen sections ----
  const printFamily = () => {
    const f = familyDetails || {};
    const secHtml = chosen.map((m) => {
      if (m.key === 'children') {
        const cur = (children.current || []).map((c) => `<li>${esc(c.name)}${c.class ? ` — ${esc(c.class)}` : ''}</li>`).join('');
        const his = (children.historical || []).map((c) => `<li style="color:#666">${esc(c.name)}${c.class ? ` — ${esc(c.class)}` : ''} <span style="font-size:11px">(left ${esc(c.leftYear)})</span></li>`).join('');
        if (!cur && !his) return '';
        return `<div class="sec"><h3>${esc(m.label)}</h3><ul>${cur}${his}</ul></div>`;
      }
      const items = m.cols
        .map((c) => ({ label: c.label, v: val(c.get(f)) }))
        .filter((x) => x.v)
        .map((x) => `<div class="row"><span class="k">${esc(x.label)}</span><span class="v">${esc(x.v)}</span></div>`)
        .join('');
      if (!items) return '';
      return `<div class="sec"><h3>${esc(m.label)}</h3>${items}</div>`;
    }).join('');
    const name = [f.FatherName, f.MotherName].filter(Boolean).join(' & ') || familyId;
    const w = window.open('', '', 'width=800,height=1000');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Family ${esc(familyId)}</title>
      <style>@page{size:A4;margin:1.4cm}*{box-sizing:border-box}
      body{font-family:Arial,'Segoe UI',sans-serif;color:#111;font-size:13px}
      h1{font-size:18px;margin:0}.hdr{border-bottom:2px solid #333;padding-bottom:8px;margin-bottom:14px}
      .muted{color:#666;font-size:12px}
      .sec{margin-bottom:14px;break-inside:avoid}.sec h3{font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#555;border-bottom:1px solid #ddd;padding-bottom:3px;margin:0 0 6px}
      .row{display:flex;justify-content:space-between;gap:16px;padding:2px 0}.k{color:#666}.v{font-weight:600;text-align:right}
      ul{margin:4px 0;padding-inline-start:18px}</style></head>
      <body><div class="hdr"><h1>${esc(name)}</h1><div class="muted">Collège des Apôtres — Family ${esc(familyId)} · ${new Date().toLocaleDateString()}</div></div>
      ${secHtml || '<p class="muted">No sections selected.</p>'}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText size={18} /> {mode === 'family' ? 'Family Report' : 'All-Families Report'}
            </h3>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Module pickers */}
        <div className="px-4 py-2 border-b bg-gray-50 flex flex-wrap gap-3 items-center">
          <span className="text-xs text-gray-500">Include:</span>
          {MODULES.map((m) => (
            <label key={m.key} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="checkbox" checked={!!sel[m.key]} onChange={() => toggle(m.key)} className="w-4 h-4" />
              {m.label}
            </label>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-4">
          {mode === 'family' ? (
            !familyId ? (
              <p className="text-gray-500 text-center py-10">Open a family first to print its sheet.</p>
            ) : (
              <div>
                <div className="flex justify-end mb-3">
                  <button onClick={printFamily} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-indigo-700">
                    <Printer size={16} /> Print / PDF
                  </button>
                </div>
                {/* Live preview of chosen sections */}
                <div className="border rounded-lg p-4 space-y-4 bg-white">
                  {chosen.length === 0 && <p className="text-gray-400 text-sm">No sections selected.</p>}
                  {chosen.map((m) => (
                    <FamilySection key={m.key} module={m} family={familyDetails || {}} children={children} />
                  ))}
                </div>
              </div>
            )
          ) : (
            <div>
              <div className="flex justify-between items-center mb-3 gap-2">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search family / name / locality…"
                  className="border rounded-lg px-3 py-1.5 text-sm w-72" />
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>{rows.length} families</span>
                  <button onClick={exportCsv} disabled={!rows.length} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg flex items-center gap-1 disabled:opacity-50">
                    <Download size={15} /> Export CSV
                  </button>
                </div>
              </div>
              {loadingAll ? (
                <p className="text-center text-gray-400 py-10">Loading…</p>
              ) : (
                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-xs whitespace-nowrap">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>{tableCols.map((c, i) => <th key={i} className="text-left px-2 py-1.5 font-medium text-gray-600 border-b">{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {rows.map((f) => (
                        <tr key={f.FamilyID} className="border-b hover:bg-gray-50">
                          {tableCols.map((c, i) => <td key={i} className="px-2 py-1.5" dir={/[؀-ۿ]/.test(val(c.get(f))) ? 'rtl' : 'ltr'}>{val(c.get(f)) || <span className="text-gray-300">—</span>}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// One section in the live preview (mirrors the printed layout).
const FamilySection = ({ module, family, children }) => {
  if (module.key === 'children') {
    const cur = children.current || [], his = children.historical || [];
    if (!cur.length && !his.length) return null;
    return (
      <div>
        <h4 className="text-[11px] uppercase tracking-wide text-gray-500 border-b pb-1 mb-1">{module.label}</h4>
        <ul className="text-sm list-disc ms-5">
          {cur.map((c, i) => <li key={`c${i}`}>{c.name}{c.class ? ` — ${c.class}` : ''}</li>)}
          {his.map((c, i) => <li key={`h${i}`} className="text-gray-500">{c.name}{c.class ? ` — ${c.class}` : ''} <span className="text-[11px]">(left {c.leftYear})</span></li>)}
        </ul>
      </div>
    );
  }
  const items = module.cols.map((c) => ({ label: c.label, v: val(c.get(family)) })).filter((x) => x.v);
  if (!items.length) return null;
  return (
    <div>
      <h4 className="text-[11px] uppercase tracking-wide text-gray-500 border-b pb-1 mb-1">{module.label}</h4>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {items.map((x, i) => (
          <div key={i} className="flex justify-between gap-3">
            <span className="text-gray-500">{x.label}</span>
            <span className="font-medium text-right" dir={/[؀-ۿ]/.test(x.v) ? 'rtl' : 'ltr'}>{x.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FamilyReportModal;
